import type { PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import type { RecommendedItem } from 'src/components/Shared/RecommendedToolsMaterials/types';
import type { ApplianceType, MediaType, MediaUploadResType } from 'src/helper/shared-types';
import { filterIssueMedia } from 'src/helper/utils';
import type {
  ApiIssueResponse,
  ApiIssueResponseSR,
  InitialIssueStateType,
  Issue,
  IssueTrip,
} from './types';

interface IssueObject {
  issue_id: string;
  title: string;
  description: string;
  work_orders: Array<{ work_order_id: string; work_order_number: string; issues_count?: number }>;
  abilities: Array<string>;
  status: {
    value: string;
    label: string;
  };
  problem: {
    category: {
      category_id: string;
      label: string;
    };
    sub_category: {
      sub_category_id: string;
      label: string;
    };
    diagnosis: {
      diagnosis_id: string;
      label: string;
    };
  };
  tripSource: {
    [key: string]: IssueTrip;
  };
  currentWorkOrderTripIds?: string[];
  relatedWorkOrderTripIds?: string[];
  // ORI-680: rides formatIssueObj's omit-spread (not filtered like `media`); declared so the
  // normalizer's contract is visible to the compiler, not just carried at runtime.
  cancellation_media?: MediaUploadResType[];
  media?: MediaType[];
  insights?: Issue['insights'];
  appliances?: ApplianceType[];
  recommended_tools?: RecommendedItem[];
  recommended_materials?: RecommendedItem[];
}

export function splitRecommendedItems(items: RecommendedItem[]) {
  return {
    recommended_tools: items.filter((item) => item.item_type === 'tool'),
    recommended_materials: items.filter((item) => item.item_type === 'material'),
  };
}

// This function restructures the issue response received from the API.
// The API includes trips related to the current work order inside the issue object.
// It also includes trips related to other work orders under a separate field.
// This function separates all trip data from the issue object.
// It returns:
// 1. A cleaned issue object with only the trip IDs (not full trip objects).
// 2. A combined array of all trips from both current and related work orders.
// This makes it easier to access and manage trips
// (e.g., storing them in tripSource as key-value pairs).
function formatIssueObj(apiIssue: ApiIssueResponse, currentIssue?: Issue): IssueObject {
  const issueObject: IssueObject = {
    ...omit(apiIssue, ['trips', 'other_work_order_trips', 'issue_recommended_items']),
    currentWorkOrderTripIds: currentIssue?.currentWorkOrderTripIds || [],
    relatedWorkOrderTripIds: currentIssue?.relatedWorkOrderTripIds || [],
    tripSource: currentIssue?.tripSource || {},
    media: filterIssueMedia(apiIssue?.media),
    ...(apiIssue?.issue_recommended_items &&
      splitRecommendedItems(apiIssue.issue_recommended_items)),
  };

  const allTripsData = [...(apiIssue?.trips || []), ...(apiIssue?.other_work_order_trips || [])];
  // normilazies the trip data to trip source
  // and spreads each trip source data if any
  const newTripSource = allTripsData?.reduce(
    (acc, trip) => {
      if (trip?.status?.value) {
        acc[trip?.trip_id] = { ...(issueObject.tripSource[trip.trip_id] || {}), ...(trip || {}) };
      }
      return acc;
    },
    {} as Issue['tripSource'],
  );
  issueObject.tripSource = { ...(issueObject?.tripSource || {}), ...(newTripSource || {}) };

  // save trip ids in currentWorkOrderTripIds and keeps existing ids
  if (Array.isArray(apiIssue?.trips)) {
    issueObject.currentWorkOrderTripIds = apiIssue?.trips.map((trip) => trip.trip_id);
  }
  // save other WO trip ids in relatedWorkOrderTripIds and keeps existing ids
  if (Array.isArray(apiIssue?.other_work_order_trips)) {
    issueObject.relatedWorkOrderTripIds = apiIssue?.other_work_order_trips.map(
      (trip) => trip.trip_id,
    );
  }

  return issueObject;
}

// This function is similar to formatIssueObj but handles ApiIssueResponseSR.
// ApiIssueResponseSR is a service request level issue that contains work_order_issues.
// Each work order issue has trips and other_work_order_trips.
// This function extracts all trips from all work order issues and combines them.
function formatIssueObjSR(
  apiIssue: ApiIssueResponseSR,
  currentIssue?: Issue,
  doNotReplaceExistingTrips = false,
): IssueObject {
  const issueObject: IssueObject = {
    ...omit(apiIssue, ['work_order_issues', 'created_at', 'issue_recommended_items']),
    currentWorkOrderTripIds: currentIssue?.currentWorkOrderTripIds || [],
    relatedWorkOrderTripIds: currentIssue?.relatedWorkOrderTripIds || [],
    tripSource: currentIssue?.tripSource || {},
    media: filterIssueMedia(apiIssue?.media || currentIssue?.media),
    ...(apiIssue?.issue_recommended_items &&
      splitRecommendedItems(apiIssue.issue_recommended_items)),
  };

  // For SR issues, collect all trip IDs from all work order issues
  const currentWorkOrderTripIds: string[] = [];

  // Extract all trips from work_order_issues
  const allTripsData: IssueTrip[] = [];
  apiIssue?.work_order_issues?.forEach((woIssue) => {
    if (woIssue?.trips) {
      allTripsData.push(
        ...woIssue.trips.map((trip) => ({
          ...trip,
          ...{
            ...(woIssue?.work_order_issue_id
              ? { work_order_issue_id: woIssue?.work_order_issue_id }
              : {}),
          },
        })),
      );
    }

    if (Array.isArray(woIssue?.trips)) {
      currentWorkOrderTripIds.push(...woIssue.trips.map((trip) => trip.trip_id));
    }
  });

  // Normalize the trip data to trip source
  const newTripSource = allTripsData?.reduce(
    (acc, trip) => {
      if (trip?.status?.value) {
        acc[trip?.trip_id] = { ...(issueObject.tripSource[trip.trip_id] || {}), ...(trip || {}) };
      }
      return acc;
    },
    {} as Issue['tripSource'],
  );
  issueObject.tripSource = { ...(issueObject?.tripSource || {}), ...(newTripSource || {}) };

  if (doNotReplaceExistingTrips) {
    const existingIds = issueObject.currentWorkOrderTripIds ?? [];
    const newIds = currentWorkOrderTripIds.filter((id) => !existingIds.includes(id));
    issueObject.currentWorkOrderTripIds = [...newIds, ...existingIds];
  } else {
    issueObject.currentWorkOrderTripIds = currentWorkOrderTripIds;
  }
  return issueObject;
}

const reducers = {
  editIssueData: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      data: Issue;
      issueId: string;
    }>,
  ) => {
    const { issueId, data } = action.payload;
    if (issueId && !isEmpty(data)) {
      const issue = formatIssueObj(data, state.issuesSource[issueId]);
      state.issuesSource[issueId] = {
        ...(state.issuesSource[issueId] || {}),
        ...issue,
      };
    }
  },

  editIssueDataFromSR: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      data: ApiIssueResponseSR;
      issueId: string;
    }>,
  ) => {
    const { issueId, data } = action.payload;
    if (issueId && !isEmpty(data)) {
      const issue = formatIssueObjSR(data, state.issuesSource[issueId]);
      state.issuesSource[issueId] = {
        ...(state.issuesSource[issueId] || {}),
        ...issue,
      };
    }
  },

  updateIssuesSource: (state: InitialIssueStateType, action: PayloadAction<ApiIssueResponse[]>) => {
    const issues = action.payload || [];
    if (isEmpty(issues)) return;
    issues.forEach((apiIssue) => {
      // Normalize and process in single pass
      const issue = formatIssueObj(apiIssue, state.issuesSource[apiIssue?.issue_id]);
      // Update issue
      state.issuesSource[issue?.issue_id] = {
        ...(state.issuesSource[issue?.issue_id] || {}),
        ...issue,
      };
    });
  },

  updateIssuesSourceFromSR: (
    state: InitialIssueStateType,
    action: PayloadAction<ApiIssueResponseSR[]>,
  ) => {
    const issues = action.payload || [];
    if (isEmpty(issues)) return;
    issues.forEach((apiIssue) => {
      // Normalize and process in single pass
      const issue = formatIssueObjSR(apiIssue, state.issuesSource[apiIssue?.issue_id]);
      // Update issue
      state.issuesSource[issue?.issue_id] = {
        ...(state.issuesSource[issue?.issue_id] || {}),
        ...issue,
      };
    });
  },

  updateIssueDataOnAssign: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      workOrder: { work_order_id: string; work_order_number: string };
      issueIds: Array<string>;
      status: { label: string; value: string };
    }>,
  ) => {
    const updates: Record<string, Issue> = {};
    for (const issueId of action?.payload?.issueIds ?? []) {
      updates[issueId] = {
        ...(state?.issuesSource?.[issueId] || {}),
        work_orders: [
          ...(state?.issuesSource?.[issueId]?.work_orders || []),
          action?.payload?.workOrder,
        ],
        status: action?.payload?.status,
      };
    }
    state.issuesSource = {
      ...state.issuesSource,
      ...updates,
    };
  },
  deleteIssueFromSource: (state: InitialIssueStateType, action: PayloadAction<string>) => {
    const issueId = action.payload;
    if (issueId && state?.issuesSource?.[issueId]) {
      const newSource = { ...state.issuesSource };
      delete newSource[issueId];
      state.issuesSource = newSource;
    }
  },

  updateIssueMedia: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      issueId: string;
      media?: MediaType;
      deleteMediaUuid?: string;
    }>,
  ) => {
    const { issueId, media, deleteMediaUuid } = action.payload;
    if (isEmpty(issueId)) return;

    const existingMedia = state.issuesSource[issueId]?.media || [];

    let updatedMedia = existingMedia;

    if (deleteMediaUuid) {
      updatedMedia = existingMedia.filter((item) => item.media_uuid !== deleteMediaUuid);
    }

    if (media) {
      updatedMedia = [...updatedMedia, media];
    }

    state.issuesSource[issueId] = {
      ...(state.issuesSource[issueId] || {}),
      media: updatedMedia,
    };
  },

  updateIssueRecommendedToolsMaterials: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      issueId: string;
      tools?: RecommendedItem[];
      materials?: RecommendedItem[];
    }>,
  ) => {
    const { issueId, tools, materials } = action.payload;
    if (!issueId || !state.issuesSource[issueId]) return;
    state.issuesSource[issueId] = {
      ...state.issuesSource[issueId],
      ...(tools !== undefined ? { recommended_tools: tools } : {}),
      ...(materials !== undefined ? { recommended_materials: materials } : {}),
    };
  },

  optimisticAddRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{
      issueId: string;
      item_type: 'tool' | 'material';
      name: string;
      tempKey: string;
    }>,
  ) => {
    const { issueId, item_type, name, tempKey } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    const newItem: RecommendedItem = { item_type, name, source: 'manual', tempKey };
    const arrayKey = item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
    issue[arrayKey] = [...(issue[arrayKey] || []), newItem];
  },

  rollbackOptimisticRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{ issueId: string; tempKey: string }>,
  ) => {
    const { issueId, tempKey } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    for (const arrayKey of ['recommended_tools', 'recommended_materials'] as const) {
      issue[arrayKey] = (issue[arrayKey] || []).filter((item) => item.tempKey !== tempKey);
    }
  },

  optimisticRemoveRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{ issueId: string; item: RecommendedItem }>,
  ) => {
    const { issueId, item } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    const arrayKey = item.item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
    issue[arrayKey] = (issue[arrayKey] || []).filter(
      (i) => i.issue_recommended_item_id !== item.issue_recommended_item_id,
    );
  },

  rollbackRemovedRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{ issueId: string; item: RecommendedItem }>,
  ) => {
    const { issueId, item } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    const arrayKey = item.item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
    const exists = (issue[arrayKey] || []).some(
      (i) => i.issue_recommended_item_id === item.issue_recommended_item_id,
    );
    if (!exists) {
      issue[arrayKey] = [...(issue[arrayKey] || []), item];
    }
  },

  optimisticUpdateRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{ issueId: string; item: RecommendedItem; newName: string }>,
  ) => {
    const { issueId, item, newName } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    const arrayKey = item.item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
    issue[arrayKey] = (issue[arrayKey] || []).map((i) =>
      i.issue_recommended_item_id === item.issue_recommended_item_id ? { ...i, name: newName } : i,
    );
  },

  rollbackUpdatedRecommendedItem: (
    state: InitialIssueStateType,
    action: PayloadAction<{ issueId: string; item: RecommendedItem }>,
  ) => {
    const { issueId, item } = action.payload;
    const issue = state.issuesSource[issueId];
    if (!issue) return;
    const arrayKey = item.item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
    issue[arrayKey] = (issue[arrayKey] || []).map((i) =>
      i.issue_recommended_item_id === item.issue_recommended_item_id
        ? { ...i, name: item.name }
        : i,
    );
  },

  updateIssueOnSRWorkOrderUpdate: (
    state: InitialIssueStateType,
    action: PayloadAction<
      Array<{
        work_order_issue_id: string;
        status: { value: string; label: string };
        issue_id: string;
        trips: IssueTrip[];
      }>
    >,
  ) => {
    type WOIssueUpdate = {
      work_order_issue_id: string;
      issue_id: string;
      trips: IssueTrip[];
      status: { value: string; label: string };
    };
    const woIssues: WOIssueUpdate[] = action.payload || [];
    if (isEmpty(woIssues)) return;

    // Group work_order_issues by issue_id
    const grouped = woIssues.reduce(
      (acc: Record<string, WOIssueUpdate[]>, woIssue: WOIssueUpdate) => {
        const { issue_id } = woIssue;
        if (!acc[issue_id]) acc[issue_id] = [];
        acc[issue_id].push(woIssue);
        return acc;
      },
      {},
    );

    Object.entries(grouped).forEach(([issueId, items]) => {
      const apiIssue = {
        issue_id: issueId,
        work_order_issues: items,
      } as ApiIssueResponseSR;

      const issue = formatIssueObjSR(apiIssue, state.issuesSource[issueId], true);
      const status = items[0]?.status;
      state.issuesSource[issueId] = {
        ...(state.issuesSource[issueId] || {}),
        ...issue,
        ...(status ? { status } : {}),
      };
    });
  },
};

export default reducers;
